import './JsonEditor.css';

import type { FocusEvent } from 'react';
import Editor from 'react-simple-code-editor';
import { Highlight, themes } from 'prism-react-renderer';
import clsx from 'clsx';
import { FilterError } from '../../../../../types/types';
import InputErrorMessage from '../../../InputErrorMessage/InputErrorMessage';

interface JsonEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (event: FocusEvent<HTMLTextAreaElement & HTMLDivElement>) => void;
  onFocus?: (event: FocusEvent<HTMLTextAreaElement & HTMLDivElement>) => void;
  disabled?: boolean;
  error?: FilterError;
  rows?: number;
  hideErrorText?: boolean;
  className?: string;
}

const highlightJson = (code: string) => (
  <Highlight code={code} language="json" theme={themes.vsLight}>
    {({ tokens, getLineProps, getTokenProps }) => (
      <>
        {tokens.map((line, i) => (
          <div key={i} {...getLineProps({ line })}>
            {line.map((token, key) => (
              <span key={key} {...getTokenProps({ token })} />
            ))}
          </div>
        ))}
      </>
    )}
  </Highlight>
);

const JsonEditor = ({
  value,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  error = null,
  rows = 5,
  hideErrorText = false,
  className,
}: JsonEditorProps) => {
  const height = `${rows * 1.5}em`;

  return (
    <div className={clsx('input-container', 'json-editor', className)}>
      <div
        className={clsx(
          'json-editor__surface',
          error && 'json-editor__surface--error',
          disabled && 'json-editor__surface--disabled'
        )}
        style={{ minHeight: height, maxHeight: height }}
      >
        <Editor
          value={value}
          onFocus={onFocus}
          onValueChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          highlight={highlightJson}
          padding={10}
          tabSize={2}
          insertSpaces
          textareaClassName="json-editor__textarea"
          preClassName="json-editor__pre"
          style={{
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        />
      </div>
      {!hideErrorText && <InputErrorMessage error={error} />}
    </div>
  );
};

export default JsonEditor;
